import axios from 'axios'
import { apiBaseUrl } from './config'

/**
 * Cycle de vie de la session du back-office.
 *
 * Le backend (Sanctum) émet des jetons à durée de vie limitée
 * (SANCTUM_TOKEN_EXPIRATION_DAYS, 7 jours par défaut) et expose une rotation
 * de jeton : `POST /auth/refresh` révoque l'ancien jeton et en émet un nouveau.
 * Ce module est la source de vérité partagée par le store d'authentification et
 * les intercepteurs HTTP : stockage du jeton, rotation et notification de fin
 * de session.
 */

export const TOKEN_STORAGE_KEY = 'goflash_token'
export const TOKEN_EXPIRY_STORAGE_KEY = 'goflash_token_expires_at'

/** Message de fin de session, conservé le temps de l'onglet (voir LoginPage). */
const AUTH_NOTICE_KEY = 'goflash_auth_notice'

/** Motifs de fin de session : expiration naturelle ou révocation (compte désactivé). */
export type SessionEndReason = 'expired' | 'revoked'

interface AuthPayload {
  token?: string
  expires_at?: string | null
}

// Client axios « nu », volontairement SANS intercepteurs : la rotation ne doit
// jamais pouvoir se rappeler elle-même ni boucler sur un 401.
const transport = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

// ─── Stockage ──────────────────────────────────────────────────────

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    // Stockage indisponible (navigation privée, quota) : session en mémoire.
    return null
  }
}

export function getStoredToken(): string | null {
  return readStorage(TOKEN_STORAGE_KEY)
}

/** Échéance ISO 8601 du jeton courant, fournie par le backend à l'émission. */
export function getStoredTokenExpiry(): string | null {
  return readStorage(TOKEN_EXPIRY_STORAGE_KEY)
}

export function storeSession(token: string, expiresAt?: string | null): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
    if (expiresAt) {
      localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiresAt)
    } else {
      localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY)
    }
  } catch {
    // Ignoré : le jeton reste valable en mémoire pour la session en cours.
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY)
  } catch {
    // Ignoré.
  }
}

// ─── Fin de session (expiration ou révocation) ─────────────────────

type SessionEndedListener = (reason: SessionEndReason) => void

const endedListeners = new Set<SessionEndedListener>()

export function onSessionEnded(listener: SessionEndedListener): () => void {
  endedListeners.add(listener)
  return () => {
    endedListeners.delete(listener)
  }
}

export function sessionEndedMessage(reason: SessionEndReason): string {
  return reason === 'revoked'
    ? 'Votre compte a été désactivé : la session a été fermée. Veuillez contacter un administrateur.'
    : 'Votre session a expiré. Veuillez vous reconnecter.'
}

/**
 * Ferme la session côté client et prévient les abonnés (store, UI).
 * Le motif est conservé dans le sessionStorage : la redirection vers /login
 * recharge la page, ce qui rendrait un message purement en mémoire invisible.
 */
export function notifySessionEnded(reason: SessionEndReason): void {
  clearSession()

  try {
    sessionStorage.setItem(AUTH_NOTICE_KEY, sessionEndedMessage(reason))
  } catch {
    // Ignoré : au pire, aucun message n'est affiché sur l'écran de connexion.
  }

  endedListeners.forEach((listener) => {
    listener(reason)
  })
}

/** Lit puis efface le message de fin de session (affiché une seule fois). */
export function consumeSessionNotice(): string | null {
  try {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY)
    if (notice) {
      sessionStorage.removeItem(AUTH_NOTICE_KEY)
    }

    return notice
  } catch {
    return null
  }
}

// ─── Rotation du jeton ─────────────────────────────────────────────

let pendingRotation: Promise<string> | null = null

/**
 * Échange le jeton courant contre un nouveau (`POST /auth/refresh`) et met à
 * jour le stockage. Les appels concurrents partagent la même requête : sans
 * cela, plusieurs 401 simultanés révoqueraient mutuellement leurs jetons.
 */
export function refreshAccessToken(): Promise<string> {
  if (!pendingRotation) {
    pendingRotation = requestRotation().finally(() => {
      pendingRotation = null
    })
  }

  return pendingRotation
}

async function requestRotation(): Promise<string> {
  const currentToken = getStoredToken()

  if (!currentToken) {
    throw new Error('Aucune session à renouveler.')
  }

  const response = await transport.post('/auth/refresh', null, {
    headers: { Authorization: `Bearer ${currentToken}` },
  })

  const payload: AuthPayload | undefined = response.data?.data

  if (!payload?.token) {
    clearSession()
    throw new Error("Le serveur n'a pas renvoyé de jeton.")
  }

  storeSession(payload.token, payload.expires_at ?? null)

  return payload.token
}
